import React, { useState, useEffect, useCallback, useRef } from 'react';
import DayView from './DayView';
import WeekView from './WeekView';
import { api } from '../../shared/utils/api';
import { dateToStr, todayStr, addDays, weekStartOf, formatWeekLabel } from './plannerUtils';

// ─── WeeklySummaryCard ────────────────────────────────────────────────────────

function WeeklySummaryCard({ summary, onDismiss }) {
  if (!summary || summary.topics_completed === 0) return null;
  const { topics_completed, topics_moved, avg_confidence, weakest_subject, weakest_subject_avg } = summary;
  return (
    <div className="planner-summary-card">
      <button className="planner-summary-dismiss" onClick={onDismiss}>×</button>
      <p className="planner-summary-text">
        Last week: <strong>{topics_completed} topics covered</strong>
        {topics_moved > 0 && `, ${topics_moved} moved forward`}.
        {avg_confidence != null && <> Average confidence: <strong>{Math.round(avg_confidence * 100)}%</strong>.</>}
        {weakest_subject && weakest_subject_avg != null && <> Weakest: <strong>{weakest_subject}</strong> ({Math.round(weakest_subject_avg * 100)}%).</>}
      </p>
    </div>
  );
}

// ─── FirstRunBanner ───────────────────────────────────────────────────────────

function FirstRunBanner({ total, onDismiss }) {
  return (
    <div className="planner-first-run-banner">
      <button className="planner-first-run-dismiss" onClick={onDismiss}>×</button>
      <p className="planner-first-run-text">
        Your revision plan is ready. {total} topics, ordered by importance. As you tick them off and add data, the plan will adjust. You can look ahead to any week, but future weeks are projections that will change.
      </p>
    </div>
  );
}

// ─── ScreenOptionsDropdown ────────────────────────────────────────────────────

function ScreenOptionsDropdown({ options, onChange, viewMode }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // The detail field for the current view
  const detailField = viewMode === 'week' ? 'weekDetail' : 'dayDetail';
  const viewLabel   = viewMode === 'week' ? 'Week view options' : 'Day view options';

  const RadioOption = ({ label, value, field }) => (
    <div className="screen-options-option" onClick={() => onChange(field, value)}>
      <div className={`screen-options-radio${options[field] === value ? ' screen-options-radio-on' : ''}`}>
        {options[field] === value && <div className="screen-options-radio-dot" />}
      </div>
      {label}
    </div>
  );

  const ToggleOption = ({ label, field }) => (
    <div className="screen-options-toggle" onClick={() => onChange(field, !options[field])}>
      {label}
      <span className="screen-options-toggle-value">{options[field] ? 'On' : 'Off'}</span>
    </div>
  );

  return (
    <div className="screen-options-wrap" ref={wrapRef}>
      <button className="screen-options-btn" onClick={() => setOpen(v => !v)}>
        <svg className="screen-options-icon" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
        Screen options
      </button>
      {open && (
        <div className="screen-options-dropdown">
          <div className="screen-options-view-label">{viewLabel}</div>
          <div className="screen-options-label">Card detail</div>
          <RadioOption label="Clean"    value="clean"    field={detailField} />
          <RadioOption label="Standard" value="standard" field={detailField} />
          <RadioOption label="Full"     value="full"     field={detailField} />
          <div className="screen-options-divider" />
          <div className="screen-options-label">Colour mode</div>
          <RadioOption label="By status"  value="status"  field="colourMode" />
          <RadioOption label="By subject" value="subject" field="colourMode" />
          <div className="screen-options-divider" />
          <ToggleOption label="Hide completed" field="hideCompleted" />
          <ToggleOption label="Sort by subject" field="sortSubject" />
        </div>
      )}
    </div>
  );
}

// ─── PlannerPage ──────────────────────────────────────────────────────────────

export default function PlannerPage({ onNavigateToTopic }) {
  const today = todayStr();

  const defaultViewMode = window.innerWidth < 768 ? 'day' : 'week';
  const [viewMode, setViewMode] = useState(defaultViewMode);
  const [currentWeekStart, setCurrentWeekStart] = useState(weekStartOf(today));
  const [currentDayStr, setCurrentDayStr] = useState(today);
  const [weekData, setWeekData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(true);
  const [firstRunDismissed, setFirstRunDismissed] = useState(
    () => localStorage.getItem('planner_first_run_dismissed') === '1'
  );
  const [showFirstRun, setShowFirstRun] = useState(false);

  const [flkFilter, setFlkFilter] = useState('all');
  const [daySort, setDaySort] = useState('priority');

  // Screen options — read from settings API on mount, persist on change
  const [screenOptions, setScreenOptions] = useState({
    weekDetail:     'standard', // clean | standard | full — week view card detail
    dayDetail:      'full',     // clean | standard | full — day view card detail
    colourMode:     'status',   // status | subject
    hideCompleted:  false,
    sortSubject:    false,
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const weekGridRef = useRef(null);
  const [weekScrollLeft, setWeekScrollLeft] = useState(false);
  const [weekScrollRight, setWeekScrollRight] = useState(false);

  // Load screen options and view mode from settings API on mount
  useEffect(() => {
    api('/api/settings').then(res => {
      if (!res || res.error) { setSettingsLoaded(true); return; }
      if (res.planner_view_mode && window.innerWidth >= 768) {
        setViewMode(res.planner_view_mode);
      }
      setScreenOptions(prev => ({
        weekDetail:    res.planner_week_detail    || prev.weekDetail,
        dayDetail:     res.planner_day_detail     || prev.dayDetail,
        colourMode:    res.planner_colour_mode    || prev.colourMode,
        hideCompleted: res.planner_hide_completed === 'true' || res.planner_hide_completed === true || prev.hideCompleted,
        sortSubject:   res.planner_sort_subject   === 'true' || res.planner_sort_subject   === true || prev.sortSubject,
      }));
      setSettingsLoaded(true);
    }).catch(() => setSettingsLoaded(true));
  }, []); // eslint-disable-line

  const handleScreenOption = useCallback((field, value) => {
    setScreenOptions(prev => {
      const next = { ...prev, [field]: value };
      // Persist to settings API
      const keyMap = {
        weekDetail:    'planner_week_detail',
        dayDetail:     'planner_day_detail',
        colourMode:    'planner_colour_mode',
        hideCompleted: 'planner_hide_completed',
        sortSubject:   'planner_sort_subject',
      };
      api('/api/settings', {
        method: 'POST',
        body: JSON.stringify({ [keyMap[field]]: String(value) }),
      }).catch(() => {});
      return next;
    });
  }, []);

  // Auto-advance to next week only when the entire current week is past
  useEffect(() => {
    const ws = weekStartOf(today);
    let pastCount = 0;
    for (let i = 0; i < 7; i++) { if (addDays(ws, i) < today) pastCount++; }
    if (pastCount >= 7) {
      const nextWs = addDays(ws, 7);
      setCurrentWeekStart(nextWs);
      setCurrentDayStr(nextWs);
    }
  }, []); // eslint-disable-line

  // Force day view on mobile
  useEffect(() => {
    const onResize = () => { if (window.innerWidth < 768) setViewMode('day'); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []); // eslint-disable-line

  // Week grid scroll button visibility
  const updateWeekScroll = useCallback(() => {
    const el = weekGridRef.current;
    if (!el) return;
    setWeekScrollLeft(el.scrollLeft > 2);
    setWeekScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = weekGridRef.current;
    if (!el) return;
    updateWeekScroll();
    el.addEventListener('scroll', updateWeekScroll, { passive: true });
    window.addEventListener('resize', updateWeekScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', updateWeekScroll);
      window.removeEventListener('resize', updateWeekScroll);
    };
  }, [weekData, updateWeekScroll]);

  const scrollWeekGrid = dir => {
    weekGridRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  const loadWeek = useCallback(async ws => {
    setLoading(true); setError(null);
    try {
      const res = await api(`/api/planner/week?date=${ws}`);
      if (!res || res.error) { setError((res && res.error) || 'Could not load planner.'); return; }
      setWeekData(res);
      setProgress(res.overall_progress);
      if (res.first_run && !firstRunDismissed) setShowFirstRun(true);
      setCurrentDayStr(prev => {
        const dates = Object.keys(res.days || {}).sort();
        return dates.includes(prev) ? prev : (dates[0] || ws);
      });
    } catch { setError('Could not load planner. Check your connection and try again.'); }
    finally { setLoading(false); }
  }, [firstRunDismissed]);

  useEffect(() => { loadWeek(currentWeekStart); }, [currentWeekStart, loadWeek]);

  useEffect(() => {
    if (weekData?.is_current_week) {
      const prevWeek = addDays(currentWeekStart, -7);
      api(`/api/planner/summary?week_start=${prevWeek}`)
        .then(res => { if (res && !res.error) setSummary(res); })
        .catch(() => {});
    }
  }, [weekData?.is_current_week, currentWeekStart]);

  const handleComplete = useCallback((entryId, conf, newProgress) => {
    setWeekData(prev => {
      if (!prev) return prev;
      const days = { ...prev.days };
      for (const ds of Object.keys(days)) {
        const idx = days[ds].entries.findIndex(e => e.id === entryId);
        if (idx !== -1) {
          const updated = days[ds].entries.map((e, i) =>
            i === idx ? { ...e, status: 'completed', confidence_on_complete: conf } : e
          );
          days[ds] = { ...days[ds], entries: updated, completed_count: updated.filter(e => e.status === 'completed').length };
          break;
        }
      }
      return { ...prev, days };
    });
    if (newProgress) setProgress(newProgress);
  }, []);

  const handleSkipDay = useCallback((_dateStr, _res) => {
    loadWeek(currentWeekStart);
  }, [currentWeekStart, loadWeek]);

  const handleMove = useCallback(async (entryId, newDate) => {
    const res = await api('/api/planner/move', {
      method: 'POST', body: JSON.stringify({ schedule_entry_id: entryId, new_date: newDate }),
    });
    if (res.ok) loadWeek(currentWeekStart);
  }, [currentWeekStart, loadWeek]);

  const handleAddEntry = useCallback((dateStr, newEntry) => {
    setWeekData(prev => {
      if (!prev || !prev.days[dateStr]) return prev;
      const entry = { id: -(Date.now()), ...newEntry, status: 'pending', confidence_on_complete: null };
      return {
        ...prev,
        days: {
          ...prev.days,
          [dateStr]: {
            ...prev.days[dateStr],
            entries: [...prev.days[dateStr].entries, entry],
            total_count: prev.days[dateStr].total_count + 1,
          },
        },
      };
    });
  }, []);

  const goToPrevWeek = () => {
    const ws = addDays(currentWeekStart, -7);
    const wsEnd = addDays(ws, 6);
    setCurrentWeekStart(ws);
    setCurrentDayStr(today >= ws && today <= wsEnd ? today : wsEnd);
  };
  const goToNextWeek = () => {
    const ws = addDays(currentWeekStart, 7);
    const wsEnd = addDays(ws, 6);
    setCurrentWeekStart(ws);
    setCurrentDayStr(today >= ws && today <= wsEnd ? today : ws);
  };
  const goToThisWeek = () => {
    setCurrentWeekStart(weekStartOf(today));
    setCurrentDayStr(today);
  };
  const navigateToDay = dateStr => {
    const ws = weekStartOf(dateStr);
    setCurrentDayStr(dateStr);
    if (ws !== currentWeekStart) setCurrentWeekStart(ws);
  };
  const dismissFirstRun = () => {
    setShowFirstRun(false);
    setFirstRunDismissed(true);
    localStorage.setItem('planner_first_run_dismissed', '1');
  };

  const isCurrentWeek = currentWeekStart === weekStartOf(today);
  const weekDates = weekData ? Object.keys(weekData.days).sort() : [];
  const currentDayData = weekData?.days?.[currentDayStr] || null;
  const pct = progress ? Math.round((progress.covered / progress.total) * 100) : 0;

  return (
    <div className="page-container page-container-wide">
    <div className="planner-page">

      {/* Header */}
      <div className="planner-top">
        <div>
          <div className="planner-title">Planner</div>
          <div className="planner-week-label">Week of {formatWeekLabel(currentWeekStart)}</div>
        </div>
        <div className="planner-top-right">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className="view-toggle">
              <button
                className={`view-btn${viewMode === 'week' ? ' view-btn-active' : ''}`}
                onClick={() => {
                  setViewMode('week');
                  api('/api/settings', { method: 'POST', body: JSON.stringify({ planner_view_mode: 'week' }) }).catch(() => {});
                }}
              >Week</button>
              <button
                className={`view-btn${viewMode === 'day' ? ' view-btn-active' : ''}`}
                onClick={() => {
                  setViewMode('day');
                  api('/api/settings', { method: 'POST', body: JSON.stringify({ planner_view_mode: 'day' }) }).catch(() => {});
                }}
              >Day</button>
            </div>
            {settingsLoaded && (
              <ScreenOptionsDropdown options={screenOptions} onChange={handleScreenOption} viewMode={viewMode} />
            )}
          </div>
          <div className="week-nav">
            <button className="week-nav-btn" onClick={goToPrevWeek}>← Prev</button>
            {!isCurrentWeek && (
              <button className="week-nav-btn week-nav-btn-active" onClick={goToThisWeek}>This week</button>
            )}
            <button className="week-nav-btn" onClick={goToNextWeek}>Next →</button>
          </div>
        </div>
      </div>

      {/* First run / summary banners */}
      {showFirstRun && !firstRunDismissed && (
        <FirstRunBanner total={progress?.total || 497} onDismiss={dismissFirstRun} />
      )}
      {weekData?.is_current_week && showSummary && summary && (
        <WeeklySummaryCard summary={summary} onDismiss={() => setShowSummary(false)} />
      )}

      {/* Progress */}
      {progress && (
        <div className="planner-progress">
          <div className="planner-progress-text">{progress.covered} of {progress.total} topics covered ({pct}%)</div>
          <div className="planner-progress-bar"><div className="planner-progress-fill" style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      {/* Feasibility / status banners */}
      {!loading && weekData?.feasibility?.status === 'tight' && (
        <div className="planner-feasibility-banner planner-feasibility-banner--tight">
          {weekData.feasibility.message}{' '}<a href="/settings" className="planner-feasibility-link">Adjust in Settings →</a>
        </div>
      )}
      {!loading && weekData?.feasibility?.status === 'unrealistic' && (
        <div className="planner-feasibility-banner planner-feasibility-banner--unrealistic">
          {weekData.feasibility.message}{' '}<a href="/settings" className="planner-feasibility-link">Adjust in Settings →</a>
        </div>
      )}
      {!loading && weekData?.is_revision_period && (
        <div className="planner-revision-period-bar">Revision period — focusing on your weakest areas</div>
      )}
      {!loading && weekData?.no_exam_date && (
        <div className="planner-no-exam-message">
          Set your exam date in Settings to generate your plan.{' '}
          <a href="/settings" className="planner-feasibility-link">Go to Settings →</a>
        </div>
      )}
      {!loading && weekData?.error_generating && !weekData?.no_exam_date && (
        <div className="planner-error">There was a problem generating your plan. Try regenerating in Settings.</div>
      )}

      {/* FLK filter */}
      {!loading && weekData && (
        <div className="planner-filters">
          {['all', 'FLK1', 'FLK2'].map(f => (
            <button
              key={f}
              className={flkFilter === f ? 'pill-filter-active' : 'pill-filter'}
              onClick={() => setFlkFilter(f)}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="planner-loading">Loading your plan</div>}
      {error && <div className="planner-error">{error}</div>}

      {/* Week view */}
      {!loading && weekData && viewMode === 'week' && (
        <WeekView
          weekData={weekData}
          weekDates={weekDates}
          today={today}
          flkFilter={flkFilter}
          detailLevel={screenOptions.weekDetail}
          colourMode={screenOptions.colourMode}
          hideCompleted={screenOptions.hideCompleted}
          sortSubject={screenOptions.sortSubject}
          onComplete={handleComplete}
          onMove={handleMove}
          onAddEntry={handleAddEntry}
          onSwitchToDayView={ds => { setCurrentDayStr(ds); setViewMode('day'); }}
          weekGridRef={weekGridRef}
          weekScrollLeft={weekScrollLeft}
          weekScrollRight={weekScrollRight}
          onScrollWeekGrid={scrollWeekGrid}
          onSkipDay={handleSkipDay}
        />
      )}

      {/* Day view */}
      {!loading && weekData && viewMode === 'day' && (
        <DayView
          dateStr={currentDayStr}
          dayData={currentDayData}
          allWeekDates={weekDates}
          onComplete={handleComplete}
          onMove={handleMove}
          onAddEntry={handleAddEntry}
          isToday={currentDayStr === today}
          isPast={currentDayStr < today}
          onPrev={() => navigateToDay(addDays(currentDayStr, -1))}
          onNext={() => navigateToDay(addDays(currentDayStr, 1))}
          daySort={daySort}
          setDaySort={setDaySort}
          detailLevel={screenOptions.dayDetail}
          colourMode={screenOptions.colourMode}
          hideCompleted={screenOptions.hideCompleted}
          sortSubject={screenOptions.sortSubject}
          flkFilter={flkFilter}
          onNavigateToTopic={onNavigateToTopic}
          onSkipDay={handleSkipDay}
        />
      )}

      {!loading && !weekData && !error && (
        <div className="planner-empty">No plan data. Your schedule will appear here once generated.</div>
      )}
    </div>
    </div>
  );
}
